/* 3D model showcase (sections/model-showcase.liquid)
   Loads Shopify's model-viewer and Shopify XR only when the section is near the screen,
   swaps the poster for the live model once it has loaded, and runs a gentle sway or turn
   that stops for good when a visitor takes control (Reset view starts it again). */
(() => {
  if (customElements.get('model-showcase')) return;

  const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  const toDegrees = (value) => {
    const v = String(value || '').trim();
    const n = parseFloat(v);
    if (Number.isNaN(n)) return null;
    if (v.endsWith('rad')) return (n * 180) / Math.PI;
    return n;
  };

  class ModelShowcase extends HTMLElement {
    connectedCallback() {
      if (!this.ready) {
        this.ready = true;
        this.setup();
      }
      // Runs again when the theme editor moves the section, so it keeps loading and moving.
      this.observe();
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', this.onMotionPreference);
    }

    setup() {
      this.stage = this.querySelector('[data-stage]');
      this.template = this.querySelector('template');
      this.resetButton = this.querySelector('[data-reset]');
      this.progressBar = this.querySelector('[data-progress]');

      const d = this.dataset;
      this.orbit = d.cameraOrbit || '40deg 74deg auto';
      this.autoRotate = d.autoRotate === 'true';
      this.rotateStyle = d.rotateStyle === 'turntable' ? 'turntable' : 'sway';
      this.speed = Math.max(1, parseFloat(d.rotateSpeed) || 10);
      this.swayRange = Math.max(5, parseFloat(d.swayRange) || 40);
      this.zoom = d.zoom === 'true';
      this.prompt = d.prompt === 'auto' ? 'auto' : 'none';

      const parts = this.orbit.split(/\s+/);
      this.baseTheta = toDegrees(parts[0]);
      this.phi = parts[1] || '75deg';
      this.radius = parts[2] || 'auto';

      this.userControl = false;
      this.inView = false;
      this.swayTime = 0;
      this.touch = null;

      if (this.resetButton) this.resetButton.addEventListener('click', () => this.reset());

      this.onMotionPreference = () => (reduceMotion.matches ? this.stopMotion() : this.startMotion());
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        this.load();
        return;
      }
      if (!this.viewer) {
        if (!this.loadObserver) {
          this.loadObserver = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.loadObserver.disconnect();
              this.load();
            }
          }, { rootMargin: '400px 0px' });
        }
        this.loadObserver.observe(this);
      }
      if (!this.viewObserver) {
        this.viewObserver = new IntersectionObserver((entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
          if (this.inView) this.startMotion();
          else this.stopMotion();
        });
      }
      this.viewObserver.observe(this.stage || this);
    }

    disconnectedCallback() {
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', this.onMotionPreference);
      this.stopMotion();
      clearTimeout(this.resumeTimer);
    }

    load() {
      if (this.viewer || !this.template || !this.stage) return;
      const source = this.template.content.querySelector('model-viewer');
      if (!source) return;

      const mv = document.importNode(source, true);
      // The section shows its own poster, so the viewer does not fetch a second one.
      mv.removeAttribute('poster');
      mv.setAttribute('reveal', 'auto');
      mv.setAttribute('loading', 'eager');
      mv.setAttribute('camera-orbit', this.orbit);
      if (!mv.getAttribute('alt') && this.dataset.alt) mv.setAttribute('alt', this.dataset.alt);
      mv.setAttribute('interaction-prompt', reduceMotion.matches ? 'none' : this.prompt);
      mv.setAttribute('disable-pan', '');
      if (!this.zoom) mv.setAttribute('disable-zoom', '');
      if (this.rotateStyle === 'turntable') {
        mv.setAttribute('rotation-per-second', `${this.speed}deg`);
        mv.setAttribute('auto-rotate-delay', '0');
      }
      // Newer model-viewer versions draw their own progress bar in this slot; ours is outside.
      const slot = document.createElement('div');
      slot.setAttribute('slot', 'progress-bar');
      mv.appendChild(slot);

      mv.addEventListener('progress', (e) => {
        const p = e.detail && typeof e.detail.totalProgress === 'number' ? e.detail.totalProgress : 0;
        if (this.progressBar) this.progressBar.style.transform = `scaleX(${p})`;
      });
      mv.addEventListener('load', () => this.onModelLoad());
      mv.addEventListener('error', () => this.onModelError());
      // model-viewer marks every camera change as user-interaction while a finger is down, even
      // when the finger only scrolls the page, so touches are judged by their own movement below.
      mv.addEventListener('camera-change', (e) => {
        if (e.detail && e.detail.source === 'user-interaction' && !this.touch) this.takeControl();
      });
      // Stop the motion as soon as a drag or arrow key starts, so it never fights the visitor.
      mv.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') {
          this.touch = { id: e.pointerId, x: e.clientX, y: e.clientY };
          return;
        }
        this.takeControl();
      }, { passive: true });
      // A finger takes control only once it moves sideways; an up or down swipe just scrolls the page.
      mv.addEventListener('pointermove', (e) => {
        const t = this.touch;
        if (!t || e.pointerId !== t.id) return;
        const dx = Math.abs(e.clientX - t.x);
        const dy = Math.abs(e.clientY - t.y);
        if (dx > 6 && dx > dy) {
          this.touch = null;
          this.takeControl();
        }
      }, { passive: true });
      const endTouch = (e) => {
        if (this.touch && e.pointerId === this.touch.id) this.touch = null;
      };
      mv.addEventListener('pointerup', endTouch, { passive: true });
      mv.addEventListener('pointercancel', endTouch, { passive: true });
      mv.addEventListener('keydown', (e) => {
        if (/^Arrow|^Page/.test(e.key)) this.takeControl();
      }, { passive: true });
      // The section draws its own focus ring around the stage, so hide model-viewer's inner one
      // (a square line clipped by the rounded corners) whenever the outer ring is showing.
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('model-viewer').then(() => {
          const root = mv.shadowRoot;
          if (!root || root.querySelector('style[data-model-showcase]')) return;
          const style = document.createElement('style');
          style.setAttribute('data-model-showcase', '');
          style.textContent = ':host(.focus-visible) .userInput, :host(:focus-visible) .userInput { outline: none; }';
          root.appendChild(style);
        });
      }

      this.viewer = mv;
      this.classList.add('is-loading');
      this.stage.appendChild(mv);

      this.loadFeature({ name: 'model-viewer', version: mv.getAttribute('data-shopify-feature') || '1.12' }, (error) => {
        if (error && !customElements.get('model-viewer')) this.onModelError();
      });
      this.setupXR();
    }

    loadFeature(feature, onLoad) {
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      try {
        shopify.loadFeatures([{ ...feature, onLoad: onLoad || (() => {}) }]);
      } catch (error) {
        // Another script already asked for a different version; that copy is used instead.
      }
    }

    setupXR() {
      if (!this.querySelector('[data-shopify-xr]')) return;
      const json = this.querySelector('script[data-models]');
      let models = [];
      try {
        models = JSON.parse(json ? json.textContent : '[]');
      } catch (error) {
        models = [];
      }
      const setup = () => {
        if (!window.ShopifyXR) return;
        if (models.length) window.ShopifyXR.addModels(models);
        window.ShopifyXR.setupXRElements();
      };
      this.loadFeature({ name: 'shopify-xr', version: '1.0' }, (error) => {
        if (error) return;
        if (window.ShopifyXR) setup();
        else document.addEventListener('shopify_xr_initialized', setup, { once: true });
      });
    }

    onModelLoad() {
      this.classList.remove('is-loading');
      this.classList.add('is-loaded');
      if (this.resetButton) this.resetButton.disabled = false;
      this.startMotion();
    }

    onModelError() {
      this.classList.remove('is-loading');
      this.classList.add('is-error');
      this.stopMotion();
    }

    takeControl() {
      if (this.userControl) return;
      this.userControl = true;
      clearTimeout(this.resumeTimer);
      this.stopMotion();
    }

    canMove() {
      return this.viewer && this.classList.contains('is-loaded') && this.autoRotate && !this.userControl && this.inView && !reduceMotion.matches;
    }

    startMotion() {
      if (!this.canMove()) return;
      const mv = this.viewer;
      if (this.rotateStyle === 'turntable') {
        mv.autoRotate = true;
        return;
      }
      if (this.frame || this.baseTheta === null) return;
      const omega = (this.speed / this.swayRange); // radians of phase per second; peak speed equals the setting
      let last = performance.now();
      const tick = (now) => {
        this.swayTime += Math.min(now - last, 100) / 1000;
        last = now;
        const theta = this.baseTheta + this.swayRange * Math.sin(this.swayTime * omega);
        mv.cameraOrbit = `${theta.toFixed(2)}deg ${this.phi} ${this.radius}`;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    stopMotion() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (this.viewer && this.rotateStyle === 'turntable') this.viewer.autoRotate = false;
    }

    reset() {
      const mv = this.viewer;
      if (!mv) return;
      this.stopMotion();
      this.userControl = false;
      this.swayTime = 0;
      if (typeof mv.resetTurntableRotation === 'function') mv.resetTurntableRotation(0);
      mv.cameraTarget = 'auto auto auto';
      mv.fieldOfView = 'auto';
      mv.cameraOrbit = this.orbit;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startMotion(), 1500);
    }
  }

  customElements.define('model-showcase', ModelShowcase);
})();
